import { assert } from "common/utils/assertOfType";
import { DIST_EPSILON } from "geom/core";
import { geomToFaces, type Geom } from "geom/geom";
import { mat4ByBasisVectors, mat4IsMirroring, mat4Mult, mat4Translate, type Mat4 } from "geom/mat4";
import { planeEval } from "geom/plane";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import type { Vec3 } from "geom/vec3";

export function caseworkLocalToWorldTransformFromHostPath(hostPath: PolyLine2, geoms: Geom[]): Mat4 | undefined {
  const xAxis = firstPathDirection(hostPath);
  const origin = firstPathPoint(hostPath);
  if (!xAxis || !origin) {
    return undefined;
  }

  const points = pointsFromGeoms(geoms);
  if (points.length === 0) {
    return undefined;
  }

  const yAxis: Vec2 = [-xAxis[1], xAxis[0]];

  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;

  for (const point of points) {
    const dx = point[0] - origin[0];
    const dy = point[1] - origin[1];
    const localX = dx * xAxis[0] + dy * xAxis[1];
    const localY = dx * yAxis[0] + dy * yAxis[1];
    minX = Math.min(minX, localX);
    minY = Math.min(minY, localY);
    minZ = Math.min(minZ, point[2]);
  }

  const pivotX = origin[0] + xAxis[0] * minX + yAxis[0] * minY;
  const pivotY = origin[1] + xAxis[1] * minX + yAxis[1] * minY;

  const localToWorldTransform = mat4Mult(
    mat4Translate(pivotX, pivotY, minZ),
    mat4ByBasisVectors(xAxis[0], xAxis[1], 0, yAxis[0], yAxis[1], 0, 0, 0, 1),
  );
  assert(!mat4IsMirroring(localToWorldTransform), "Casework local transform should not mirror geometry");
  return localToWorldTransform;
}

function firstPathPoint(path: PolyLine2): Vec2 | undefined {
  if (path.length < 2) {
    return undefined;
  }

  return [path[0], path[1]];
}

function firstPathDirection(path: PolyLine2): Vec2 | undefined {
  for (let i = 0; i + 3 < path.length; i += 2) {
    const dx = path[i + 2] - path[i];
    const dy = path[i + 3] - path[i + 1];
    const length = Math.hypot(dx, dy);
    if (length > DIST_EPSILON) {
      return [dx / length, dy / length];
    }
  }

  return undefined;
}

function pointsFromGeoms(geoms: Geom[]): Vec3[] {
  const points: Vec3[] = [];
  for (const geom of geoms) {
    for (const face of geomToFaces(geom)) {
      for (const loop of face.loops) {
        for (let i = 0; i + 1 < loop.length; i += 2) {
          const [x, y, z] = planeEval(face.plane, loop[i], loop[i + 1]);
          points.push([x, y, z]);
        }
      }
    }
  }

  return points;
}
